// Small always-present pieces: the toast and the connection pill.
import { useEffect, useState } from "react";
import { useConvex } from "convex/react";
import { Icon } from "./Icon";
import { update, useStore } from "../lib/store";

export function ToastHost() {
  const t = useStore((st) => st.toast);
  if (!t) return null;
  return (
    <div className="toast" role="status" key={t.id}>
      {t.icon && <Icon name={t.icon} size={16} />}
      <span className="tm">{t.text}</span>
      {t.undo && <button className="tu" onClick={() => { t.undo!(); update({ toast: null }); }}>{t.action ?? "Undo"}<kbd>Z</kbd></button>}
    </div>
  );
}

/** Quiet unless something is wrong: offline or reconnecting, then a brief "Back online". */
export function ConnectionPill() {
  const convex = useConvex();
  const [state, setState] = useState<"ok" | "offline" | "back">("ok");
  useEffect(() => {
    let wasDown = false;
    const tick = () => {
      const cs = convex.connectionState();
      const down = !navigator.onLine || (cs.hasEverConnected && !cs.isWebSocketConnected);
      if (down) { wasDown = true; setState("offline"); }
      else if (wasDown) { wasDown = false; setState("back"); setTimeout(() => setState((s) => (s === "back" ? "ok" : s)), 2400); }
    };
    const id = setInterval(tick, 1500);
    addEventListener("online", tick); addEventListener("offline", tick);
    return () => { clearInterval(id); removeEventListener("online", tick); removeEventListener("offline", tick); };
  }, [convex]);
  if (state === "ok") return null;
  return (
    <div className={`netpill ${state}`} role="status">
      <Icon name={state === "offline" ? "wifiOff" : "check"} size={16} />
      {state === "offline" ? "Offline · reconnecting. Changes are kept and sent when you're back." : "Back online"}
    </div>
  );
}

/**
 * One tooltip for every control with data-tip (and an optional data-key shortcut). The first
 * waits a moment, so passing over a control shows nothing; once one is up, moving on to the
 * next control shows its tip at once, until the pointer has rested off them for a moment.
 */
export function TooltipHost() {
  const [tip, setTip] = useState<{ text: string; key?: string; x: number; y: number } | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let warmUntil = 0; // reading tips: the next one needs no wait
    const show = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      warmUntil = Infinity;
      setTip({ text: el.dataset.tip!, key: el.dataset.key, x: Math.min(innerWidth - 120, Math.max(8, r.left + r.width / 2)), y: r.bottom + 6 });
    };
    const over = (e: MouseEvent) => {
      const el = (e.target as Element).closest?.<HTMLElement>("[data-tip]");
      clearTimeout(timer);
      if (!el) { if (warmUntil === Infinity) warmUntil = Date.now() + 600; setTip(null); return; }
      if (Date.now() < warmUntil) show(el);
      else timer = setTimeout(() => show(el), 450);
    };
    const hide = () => { clearTimeout(timer); warmUntil = 0; setTip(null); };
    addEventListener("mouseover", over); addEventListener("mousedown", hide); addEventListener("keydown", hide); addEventListener("scroll", hide, true);
    return () => { removeEventListener("mouseover", over); removeEventListener("mousedown", hide); removeEventListener("keydown", hide); removeEventListener("scroll", hide, true); };
  }, []);
  if (!tip) return null;
  return <div className="tip" style={{ left: tip.x, top: tip.y, transform: "translateX(-50%)" }} role="tooltip">{tip.text}{tip.key && <kbd>{tip.key}</kbd>}</div>;
}
